import type { ASTFileResult, ASTAlignedLine, ASTChange, ASTLineSide, AlignedLinePair } from './astDiffTypes'

export interface ASTFileParsed {
  path: string
  language: string
  status: string
  error?: string
  rows: ASTRow[]
}

export type ASTRow =
  | { kind: 'separator'; hiddenCount: number }
  | { kind: 'del'; lhsLine: number; lhsChanges: ASTChange[] }
  | { kind: 'add'; rhsLine: number; rhsChanges: ASTChange[] }
  | { kind: 'pair'
      lhsLine: number; lhsChanges: ASTChange[]
      rhsLine: number; rhsChanges: ASTChange[]
    }

// parseASTDiff converts the raw JSON string from Revision.astDiffJson into
// an array of ASTFileParsed ready for rendering. Returns [] for empty/invalid input.
export function parseASTDiff(astDiffJson: string | undefined): ASTFileParsed[] {
  if (!astDiffJson) return []

  let entries: ASTFileResult[]
  try {
    entries = JSON.parse(astDiffJson) as ASTFileResult[]
  } catch {
    return []
  }

  return entries.map(entry => parseFile(entry))
}

function parseFile(entry: ASTFileResult): ASTFileParsed {
  const base: Omit<ASTFileParsed, 'rows'> = {
    path: entry.path,
    language: entry.language,
    status: entry.status,
    error: entry.error || undefined,
  }

  if (entry.error) {
    return { ...base, rows: [] }
  }

  if (entry.status === 'unchanged' || !entry.chunks) {
    return { ...base, rows: [] }
  }

  const rows: ASTRow[] = []
  const alignedLines = entry.aligned_lines ?? []
  const chunks = entry.chunks

  // Compute the line-number ranges each chunk covers so we can insert separators.
  // A chunk covers the range of lhs/rhs line numbers of its lines.
  let prevChunkMaxLine = -1 // tracks highest rhs line seen (for separator counting)

  for (let ci = 0; ci < chunks.length; ci++) {
    const chunk = chunks[ci]

    // Count hidden lines between this chunk and the previous one using aligned_lines.
    const chunkMinLine = firstLine(chunk)
    if (ci > 0 && alignedLines.length > 0) {
      const hidden = countHiddenLines(alignedLines, prevChunkMaxLine, chunkMinLine)
      if (hidden > 0) {
        rows.push({ kind: 'separator', hiddenCount: hidden })
      }
    }

    for (const alignedLine of chunk) {
      rows.push(buildRow(alignedLine))
    }

    prevChunkMaxLine = lastLine(chunk)
  }

  // Separator after last chunk if there are trailing unchanged lines.
  if (alignedLines.length > 0 && prevChunkMaxLine >= 0) {
    const trailing = countHiddenLines(alignedLines, prevChunkMaxLine, Infinity)
    if (trailing > 0) {
      rows.push({ kind: 'separator', hiddenCount: trailing })
    }
  }

  return { ...base, rows }
}

function buildRow(line: ASTAlignedLine): ASTRow {
  const { lhs, rhs } = line
  if (lhs && rhs) {
    return {
      kind: 'pair',
      lhsLine: lhs.line_number + 1,
      lhsChanges: lhs.changes,
      rhsLine: rhs.line_number + 1,
      rhsChanges: rhs.changes,
    }
  }
  if (lhs) {
    return { kind: 'del', lhsLine: lhs.line_number + 1, lhsChanges: lhs.changes }
  }
  // rhs only
  const r = rhs as ASTLineSide
  return { kind: 'add', rhsLine: r.line_number + 1, rhsChanges: r.changes }
}

// firstLine returns the minimum (0-based) rhs or lhs line number in a chunk.
function firstLine(chunk: ASTAlignedLine[]): number {
  for (const l of chunk) {
    if (l.rhs) return l.rhs.line_number
    if (l.lhs) return l.lhs.line_number
  }
  return 0
}

// lastLine returns the maximum (0-based) rhs or lhs line number in a chunk.
function lastLine(chunk: ASTAlignedLine[]): number {
  let max = 0
  for (const l of chunk) {
    const n = l.rhs?.line_number ?? l.lhs?.line_number ?? 0
    if (n > max) max = n
  }
  return max
}

// countHiddenLines counts aligned_lines entries whose rhs line is strictly between
// afterLine and beforeLine (0-based, exclusive on both ends).
function countHiddenLines(
  alignedLines: AlignedLinePair[],
  afterLine: number,
  beforeLine: number,
): number {
  if (!isFinite(beforeLine)) {
    // trailing: count lines after afterLine
    return alignedLines.filter(([, rhs]) => rhs !== null && rhs > afterLine).length
  }
  return alignedLines.filter(([, rhs]) => rhs !== null && rhs > afterLine && rhs < beforeLine).length
}
